import { useEffect, useMemo, useRef } from 'react';
import { parseYoutubeId } from '@/lib/youtube';
import { loadYoutubeIframeApi, type YoutubeReplayPlayer } from '@/lib/youtubeIframeApi';

const POLL_INTERVAL_MS = 250;
// Avance normale entre deux ticks ~0,25s ; un bond plus grand = scrub utilisateur (avant ou arrière).
const SEEK_THRESHOLD_SECONDS = 1;

type Props = {
  youtubeUrl?: string | null;
  onTimeUpdate: (seconds: number, options: { isSeek: boolean }) => void;
  onEnded: () => void;
};

export default function YouTubeReplayPlayer({ youtubeUrl, onTimeUpdate, onEnded }: Props) {
  const videoId = useMemo(() => parseYoutubeId(youtubeUrl), [youtubeUrl]);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const playerRef = useRef<YoutubeReplayPlayer | null>(null);
  const lastSecondsRef = useRef<number | null>(null);
  const onTimeUpdateRef = useRef(onTimeUpdate);
  onTimeUpdateRef.current = onTimeUpdate;
  const onEndedRef = useRef(onEnded);
  onEndedRef.current = onEnded;

  useEffect(() => {
    if (!videoId || !containerRef.current) return;

    let destroyed = false;
    const mountPoint = document.createElement('div');
    containerRef.current.appendChild(mountPoint);

    loadYoutubeIframeApi()
      .then((api) => {
        if (destroyed) return;
        playerRef.current = new api.Player(mountPoint, {
          videoId,
          playerVars: {
            rel: 0,
            iv_load_policy: 3,
            playsinline: 1,
            modestbranding: 1,
            controls: 1,
          },
          events: {
            onStateChange: (event) => {
              if (event.data === api.PlayerState.ENDED) {
                onEndedRef.current();
              }
            },
          },
        });
      })
      .catch(() => {});

    return () => {
      destroyed = true;
      try {
        playerRef.current?.destroy();
      } catch {
        /* ignore */
      }
      playerRef.current = null;
      mountPoint.remove();
    };
  }, [videoId]);

  useEffect(() => {
    if (!videoId) return;
    const interval = window.setInterval(() => {
      const player = playerRef.current;
      const seconds = player?.getCurrentTime?.();
      if (typeof seconds !== 'number' || Number.isNaN(seconds)) return;

      const previous = lastSecondsRef.current;
      const isSeek = previous != null && Math.abs(seconds - previous) > SEEK_THRESHOLD_SECONDS;
      lastSecondsRef.current = seconds;
      onTimeUpdateRef.current(seconds, { isSeek });
    }, POLL_INTERVAL_MS);

    return () => window.clearInterval(interval);
  }, [videoId]);

  return <div ref={containerRef} className="w-full h-full [&>iframe]:w-full [&>iframe]:h-full" />;
}
